import BackButton from '@components/backButton/BackButton'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { FC, ReactNode, Suspense } from 'react'
import { createPortal } from 'react-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { cn } from '@/lib/utils'

const DetailView = ({ children }: { children: React.ReactNode }) => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div>{children}</div>
    </Suspense>
  )
}

/*  Header  ---------------------------------------------------------------------  */

const DetailViewHeader: FC<{ children: React.ReactNode }> = ({ children }) => {
  const header = document.getElementById('header')
  if (!header) return null
  return createPortal(
    <h1 className="m-0 font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2.25rem]">
      {children}
    </h1>,
    header
  )
}

/*  ActionBar  ------------------------------------------------------------------  */

const DetailViewActionBar: FC<{ children?: React.ReactNode }> = ({
  children
}) => {
  return (
    <div className="flex items-center justify-between gap-3">
      <BackButton />
      {children}
    </div>
  )
}

/*  Breadcrumb  -----------------------------------------------------------------  */

/**
 * Circular back button + breadcrumb row used at the top of every admin
 * detail/create view. Replaces the hand-rolled markup duplicated across them.
 */
const DetailViewBreadcrumb: FC<{
  parentLabel: ReactNode
  parentTo: string
  current: ReactNode
  className?: string
  children?: ReactNode
}> = ({ parentLabel, parentTo, current, className, children }) => {
  const navigate = useNavigate()
  const { t } = useTranslation('layout')
  return (
    <div className={cn('mb-5 flex min-w-0 items-center gap-3', className)}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        aria-label={t('common.back')}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ink/15 bg-white p-0 text-ink transition-colors hover:border-ink/30 hover:bg-ink/[0.03]"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
      <nav className="flex min-w-0 items-center gap-1.5 text-sm">
        <Link
          to={parentTo}
          className="shrink-0 text-muted-foreground transition-colors hover:text-primary"
        >
          {parentLabel}
        </Link>
        <ChevronRight className="h-4 w-4 shrink-0 text-ink/30" />
        <span className="truncate font-semibold text-ink">{current}</span>
      </nav>
      {children}
    </div>
  )
}

/*  Content  --------------------------------------------------------------------  */

const DetailViewContent: FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div>{children}</div>
}

/*  -----------------------------------------------------------------------------  */

export const withDetailView = (
  WrappedComponent: React.ComponentType<any>,
  title?: string
) => {
  return function DetailViewWrapper(props: any) {
    return (
      <ErrorBoundary fallback={<div>Error</div>}>
        <DetailView>
          {title && <DetailViewHeader>{title}</DetailViewHeader>}
          <WrappedComponent {...props} />
        </DetailView>
      </ErrorBoundary>
    )
  }
}

const Root = DetailView
const Header = DetailViewHeader
const Content = DetailViewContent
const ActionBar = DetailViewActionBar
const Breadcrumb = DetailViewBreadcrumb

export { ActionBar, Breadcrumb, Content, Header, Root }
